import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PAGE_HELP, type HelpSection, type HelpTerm } from "./content";

/**
 * The per-page help panel.
 *
 * It answers the four questions an operator actually has, in the order they
 * have them: what am I looking at, what do the controls do, how do I read the
 * result, and what should I do about it. The last is the one help texts usually
 * omit and the only one anybody opened the panel for.
 *
 * The trigger is HeaderHelpButton, one control in the console header that
 * decides the page from the URL (see routeHelp.ts). This component is the
 * dialog only, and it is controlled: whoever opens it owns the state.
 *
 * A page id with no entry renders NOTHING rather than an empty panel. A help
 * dialog that opens to nothing is worse than none: it costs a click and teaches
 * the reader that the help is not worth opening. The guard tests in
 * __tests__/routeHelp.test.ts make the omission visible instead.
 */
export function HelpDialog({
  page,
  open,
  onOpenChange,
}: {
  page: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const help = PAGE_HELP[page];
  if (help === undefined) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-2xl overflow-y-auto"
        data-testid={`page-help-${page}`}
      >
        <DialogHeader>
          <DialogTitle>{help.title}</DialogTitle>
          <DialogDescription>{help.purpose}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 text-sm">
          {help.sections.map((s) => (
            <Section key={s.title} section={s} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ section }: { section: HelpSection }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
        {section.title}
      </h3>

      {section.body?.map((p, i) => (
        <p key={i} className="max-w-prose text-ink-2">
          {p}
        </p>
      ))}

      {section.terms !== undefined && section.terms.length > 0 ? (
        <dl className="flex flex-col divide-y divide-border rounded-md border border-border">
          {section.terms.map((t) => (
            <Term key={t.term} term={t} />
          ))}
        </dl>
      ) : null}
    </section>
  );
}

function Term({ term }: { term: HelpTerm }) {
  return (
    <div className="grid gap-1 p-3 sm:grid-cols-[minmax(9rem,12rem)_1fr] sm:gap-3">
      <dt className="font-medium text-ink">{term.term}</dt>
      <dd className="text-ink-2">
        {term.meaning}
        {/* The caution is what stops a plausible misreading, so it is set apart
            rather than appended to the sentence somebody has already skimmed. */}
        {term.caution !== undefined ? (
          <span className="mt-1 block border-l-2 border-state-accepted/50 pl-2 text-ink-3">
            {term.caution}
          </span>
        ) : null}
      </dd>
    </div>
  );
}
